import type { AttachmentLink, Message } from "@/v5/components/ConversationThread";
import type { CommlogEntry, HealthNexusMessage } from "@/v5/hooks/useCommlog";
import { splitRawContent } from "@/lib/utils";
import { isReadSource } from "@/lib/readSource";
import { parseInertHtml } from "@/lib/inertHtml";

export const COMMLOG_TYPE_ID_MAP: Record<string, Message["channel"]> = {
  "0": "note",
  "1": "email",
  "2": "sms",
  "3": "phone",
  "4": "note",
  "7": "email",
  "17": "note",
  "19": "note",
  "18": "sms",
  "21": "phone",
  "22": "whatsapp",
  // 25/26 are synthetic, client-side only: marketing campaign / automation
  // sends derived from the marketing send logs. They are never stored in
  // comm_log, so these ids can never collide with a real comm_type. Both share
  // one channel so a single "Marketing" filter pill covers e-mail and SMS.
  "25": "marketing",
  "26": "marketing",
  // 27 is synthetic too: the patient requested information through a
  // request-info link in a marketing message (see useCommlog). Same channel, so
  // the Marketing pill shows it beside the send that carried the link.
  "27": "marketing",
};

/**
 * Delivery states worth putting on a marketing entry. "sent" and "delivered"
 * are deliberately absent: the ordinary case should stay quiet rather than
 * badge every campaign message. Everything here is either still in flight or
 * did not reach the patient, which is exactly what staff need to know when a
 * patient says they never got something.
 */
const MARKETING_STATUS_LABELS: Record<string, string> = {
  sending: "Sending",
  pending: "Pending",
  failed: "Failed",
  undelivered: "Not delivered",
  bounce: "Bounced",
  dropped: "Dropped",
  invalid: "Invalid number",
  unsubscribed: "Unsubscribed",
};

/**
 * What the reader is told when a message never reached the patient, per channel.
 *
 * Two halves on purpose: what went wrong, and what to do about it. The advice
 * differs by channel because the useful next step does — a WhatsApp message
 * that did not arrive can simply be sent as a text, whereas a text that did not
 * arrive usually means the number itself needs checking.
 *
 * Deliberately says nothing about WHY the provider refused it. The reason is
 * worth having in the logs and is useless on screen: nobody at a front desk can
 * act on a delivery code, and every one of them maps to one of these two steps.
 */
const DELIVERY_FAILURE_COPY: Record<"whatsapp" | "sms", string> = {
  whatsapp: "WhatsApp didn't deliver this. Send it as a text message instead.",
  sms: "This text didn't reach the patient. Check their number, then try again.",
};

/**
 * What to say when the API could name WHY a WhatsApp message did not arrive.
 *
 * Keyed by the reason the API sends, which comes from the provider's own
 * rejection code. Every one of these has to say what the reader should do
 * next, and none of them may name the mechanism behind it — see the banned
 * vocabulary pinned in ConversationThread.deliveryFailure.test.tsx. "Approved
 * message" is the term the composer already uses.
 *
 * A reason not listed here falls back to DELIVERY_FAILURE_COPY.whatsapp, so an
 * API that learns a new one before the client does degrades to today's
 * sentence rather than to a blank.
 */
const WHATSAPP_FAILURE_REASON_COPY: Record<string, string> = {
  "window-closed":
    "WhatsApp only lets you message a patient for a day after they message you, " +
    "and this patient hasn't recently. Send this as a text message instead.",
  "rate-limited":
    "WhatsApp is limiting how many people this practice can message right now. " +
    "Send this as a text message, or try again later.",
  "template-rejected":
    "WhatsApp would not accept that approved message. Pick a different one, " +
    "or send this as a text message.",
};

/** Badge text for a message that did not arrive. */
const DELIVERY_FAILURE_LABEL = "Not delivered";

/**
 * The most specific advice we can give for a message that did not arrive.
 *
 * A named WhatsApp reason wins; otherwise the channel's general sentence. A
 * text message never takes a reason — the API only names causes for WhatsApp,
 * and an SMS failure already has one thing to check.
 */
function deliveryFailureAdvice(kind: string, reason: string): string {
  if (kind === "whatsapp") {
    return WHATSAPP_FAILURE_REASON_COPY[reason] ?? DELIVERY_FAILURE_COPY.whatsapp;
  }

  return DELIVERY_FAILURE_COPY.sms;
}

function firstNonEmpty(...values: unknown[]): string {
  for (const value of values) {
    const text = String(value ?? "").trim();
    if (text) {
      return text;
    }
  }
  return "";
}

function isTruthyFlag(value: unknown): boolean {
  return value === true || value === 1 || value === "1";
}

function isHtml(value: string): boolean {
  return /<[a-z][\s\S]*>/i.test(value);
}

/**
 * Form-submission comm log entries (subject "Patient Form Submitted") store
 * their body as a single legacy anchor pointing at
 * `patient_details.php?...&responseID=<surveysResponseID>`. The responseID is
 * the surveysResponseID that the V5 SurveyResponseModal needs to render the
 * actual submitted answers. Parse it out so the Comm Log can open the real
 * form viewer instead of the dead legacy link in an iframe.
 */
function parseSurveyResponseId(value: string): number | undefined {
  if (!value) return undefined;
  const match = value.match(/patient_details\.php\?[^"'<>]*responseID=(\d+)/i);
  if (!match) return undefined;
  const id = Number(match[1]);
  return Number.isFinite(id) && id > 0 ? id : undefined;
}

function hasEncodedHtml(value: string): boolean {
  return /&lt;[a-z][\s\S]*&gt;/i.test(value);
}

function decodeHtmlEntities(value: string): string {
  if (!value) return "";
  const tmp = document.createElement("textarea");
  tmp.innerHTML = value;
  return tmp.value;
}

function stripHtml(value: string): string {
  if (!value) return "";
  const tmp = parseInertHtml(value);
  // textContent includes the TEXT INSIDE <style>/<script>, so any HTML carrying
  // a stylesheet leaks its CSS into the preview — e.g. a reply that quotes a
  // marketing email renders as "p { margin: 0 } td { ... }" before the
  // patient's actual words. Remove those elements before reading text.
  // The parse is inert (no handlers run), so this is only about text.
  tmp.querySelectorAll("style, script, title").forEach((node) => node.remove());
  const raw = tmp.textContent || tmp.innerText || "";
  return raw.replace(/\s+/g, " ").trim();
}

/**
 * Remove the quoted reply chain from an HTML email body.
 *
 * Clients disagree on how they mark it:
 *  - Gmail / Apple Mail / Thunderbird wrap it in <blockquote> (often with
 *    .gmail_quote or .moz-cite-prefix), so removing the element is enough.
 *  - Outlook does NOT use blockquote. It appends an #appendonsend marker, a
 *    divider, a #divRplyFwdMsg header block, then the original body as ordinary
 *    sibling divs — so everything from that marker onward has to go.
 *
 * Anything this misses is caught by the text-level stripEmailHeaders() pass,
 * which truncates at the "From: … Subject: …" header block.
 */
function stripQuotedHtml(value: string): string {
  const tmp = parseInertHtml(value);

  tmp.querySelectorAll("blockquote, .gmail_quote, .moz-cite-prefix, .OutlookMessageHeader")
    .forEach((node) => node.remove());

  // Outlook: drop the marker/header and every following sibling.
  const cutFrom = tmp.querySelector("#appendonsend, #divRplyFwdMsg");
  if (cutFrom) {
    let node: ChildNode | null = cutFrom;
    while (node) {
      const next: ChildNode | null = node.nextSibling;
      node.remove();
      node = next;
    }
  }

  return tmp.innerHTML;
}

const CP1252: Record<number, number> = {
  0x80: 0x20AC,
  0x82: 0x201A,
  0x83: 0x0192,
  0x84: 0x201E,
  0x85: 0x2026,
  0x86: 0x2020,
  0x87: 0x2021,
  0x88: 0x02C6,
  0x89: 0x2030,
  0x8A: 0x0160,
  0x8B: 0x2039,
  0x8C: 0x0152,
  0x8E: 0x017D,
  0x91: 0x2018,
  0x92: 0x2019,
  0x93: 0x201C,
  0x94: 0x201D,
  0x95: 0x2022,
  0x96: 0x2013,
  0x97: 0x2014,
  0x98: 0x02DC,
  0x99: 0x2122,
  0x9A: 0x0161,
  0x9B: 0x203A,
  0x9C: 0x0153,
  0x9E: 0x017E,
  0x9F: 0x0178,
};

const UNICODE_TO_CP1252 = new Map<number, number>();
for (const [byte, cp] of Object.entries(CP1252)) {
  UNICODE_TO_CP1252.set(cp, Number(byte));
}

function fixEncoding(value: string): string {
  if (!value) return "";
  let current = value;
  for (let round = 0; round < 3; round++) {
    if (!/Ã[^\x00-\x7F]|Â[^\x00-\x7F]|ï¼/.test(current)) break;

    const bytes: number[] = [];
    let valid = true;
    for (const ch of current) {
      const cp = ch.codePointAt(0)!;
      if (cp <= 0x7F || cp <= 0xFF) {
        bytes.push(cp);
        continue;
      }
      const byte = UNICODE_TO_CP1252.get(cp);
      if (byte === undefined) {
        valid = false;
        break;
      }
      bytes.push(byte);
    }

    if (!valid) break;

    try {
      const decoded = new TextDecoder("utf-8", { fatal: true }).decode(new Uint8Array(bytes));
      if (decoded === current) break;
      current = decoded;
    } catch {
      break;
    }
  }
  return current;
}

export function normalizeBody(value: string, hideQuoted?: boolean): string {
  if (!value) {
    return "";
  }

  let normalized = value;
  if (!isHtml(normalized) && hasEncodedHtml(normalized)) {
    normalized = decodeHtmlEntities(normalized);
  }
  if (hideQuoted && isHtml(normalized)) {
    normalized = stripQuotedHtml(normalized);
  }
  return normalized.trim();
}

function cleanText(value: string): string {
  return fixEncoding(stripHtml(value)).trim();
}

/**
 * Strip inline email header blocks, quoted reply chains, and signature
 * placeholders from the collapsed single-line preview text produced by
 * cleanText/stripHtml.
 *
 * cleanText collapses all whitespace to single spaces, so by this point
 * the text is one continuous line like:
 *   "Thanks so much Aspasia signature_2279893417 From: ... Subject: Re: ... Okay great..."
 *
 * Everything from the first "From:" header onward is the quoted reply
 * chain (headers + previous messages). We strip it all, keeping only
 * the new content that precedes it.
 */
function stripEmailHeaders(text: string): string {
  if (!text) return text;

  let result = text;

  // Strip signature placeholders (e.g. "signature_2279893417").
  result = result.replace(/\bsignature_\d+\b/gi, "");

  // The inline header block (From: ... Date: ... To: ... Subject: ...)
  // marks the start of the quoted reply chain. Truncate everything from
  // that point onward. We require "From:" followed eventually by at
  // least one more header keyword to avoid false positives on casual
  // use of "From:" in message text.
  const headerStart = result.search(
    /\bFrom:\s.*?\b(?:Date|To|Cc|Bcc|Subject|Sent|Reply-To):/i,
  );
  if (headerStart > 0) {
    result = result.substring(0, headerStart);
  } else if (headerStart === 0) {
    // The entire text starts with headers (no body before them). In
    // this case try to find the body after "Subject:" value.
    const afterSubject = result.replace(
      /^.*?\bSubject:\s*(?:(?:Re|Fwd?):\s*)*/i,
      "",
    );
    if (afterSubject && afterSubject !== result) {
      result = afterSubject;
    }
  }

  // Also strip "On <date> <name> wrote:" attribution that can appear
  // before the header block in some reply formats.
  result = result.replace(
    /\bOn\s+(?:Sun|Mon|Tue|Wed|Thu|Fri|Sat)\w*,?\s+.+?\bwrote:\s*/gi,
    "",
  );

  // Collapse leftover multiple spaces and trim.
  return result.replace(/\s{2,}/g, " ").trim();
}

function fallbackPreview(channel: Message["channel"], subject: string, hasHtmlContent: boolean): string {
  if (subject) {
    return subject;
  }

  switch (channel) {
    case "email":
      return hasHtmlContent ? "(Email content available)" : "(No email content)";
    case "note":
      return hasHtmlContent ? "(Note content available)" : "(No note text)";
    case "phone":
      return "(Call log entry)";
    case "video":
      return "(Video log entry)";
    default:
      return "(No message text)";
  }
}

function buildStageChangeContent(typeID: string, stageName: string, notes: string): string {
  const base = stageName
    ? typeID === "19"
      ? `Stage change cancelled: ${stageName}`
      : `Stage changed to ${stageName}`
    : typeID === "19"
      ? "Stage change cancelled"
      : "Stage changed";
  const body = cleanText(notes);
  return body ? `${base} — ${body}` : base;
}

function parseAttachmentLinks(
  entry: Record<string, unknown>,
  patientId?: string,
): Array<AttachmentLink> {
  if (!patientId) {
    return [];
  }

  const raw = String(entry.attachments ?? "").trim();
  if (!raw) {
    return [];
  }

  const patientIdNum = Number(patientId);

  return raw
    .split(/[\r\n,]+/)
    .map((value) => value.trim())
    .filter(Boolean)
    .map((value) => {
      const normalized = value.replace(/^\/+/, "");
      const label = normalized.split("/").pop() || normalized;
      if (normalized.startsWith("http")) {
        return { label, href: normalized };
      }
      // comm_log.attachments stores paths like "{patientId}/attachments/{file}"
      // or "{patientId}/uploads/{file}". Extract the subfolder and filename so
      // the UI can fetch the file through the authenticated v5
      // PatientFilesController::download endpoint via apiClient.
      const segments = normalized.split("/");
      const fileName = segments.pop() || normalized;
      // The first segment is the patientId; everything between is the subfolder.
      const folder = segments.length > 1 ? segments.slice(1).join("/") : "";
      return { label, patientId: patientIdNum, name: fileName, folder };
    });
}

function authorNameForEntry(
  entry: Record<string, unknown>,
  sender: Message["sender"],
  isStreamEntry: boolean,
  patientName?: string,
  marketingKind?: Message["marketingKind"],
): string {
  if (sender === "patient") {
    return (patientName ?? "").trim() || "Patient";
  }
  // Marketing sends set isStream to inherit read-only behaviour, so they have to
  // be named ahead of the stream branch — otherwise every campaign e-mail is
  // attributed to "SMS Stream". Name the source, the way streams name theirs.
  if (marketingKind === "email" || marketingKind === "sms") {
    return String(entry.marketingSourceKind ?? "") === "automation"
      ? "Marketing Automation"
      : "Marketing Campaign";
  }
  if (isStreamEntry) {
    return "SMS Stream";
  }
  const authorName = [String(entry.authorFirstName ?? ""), String(entry.authorLastName ?? "")]
    .join(" ")
    .trim();
  if (authorName) return authorName;
  const userId = Number(entry.userID ?? entry.userId ?? 0);
  return userId === 0 ? "Prospera" : "Staff";
}

export function normalizeTimestamp(raw: unknown): string {
  const value = String(raw ?? "").trim();
  if (!value) {
    return "";
  }

  const isoMatch = value.match(/\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}/);
  if (isoMatch) {
    return isoMatch[0].replace(" ", "T");
  }

  const usMatch = value.match(/\d{1,2}\/\d{1,2}\/\d{4}\s+\d{1,2}:\d{2}(?::\d{2})?/);
  if (usMatch) {
    const parsed = new Date(usMatch[0]);
    if (!Number.isNaN(parsed.getTime())) {
      const year = parsed.getFullYear();
      const month = String(parsed.getMonth() + 1).padStart(2, "0");
      const day = String(parsed.getDate()).padStart(2, "0");
      const hour = String(parsed.getHours()).padStart(2, "0");
      const minute = String(parsed.getMinutes()).padStart(2, "0");
      const second = String(parsed.getSeconds()).padStart(2, "0");
      return `${year}-${month}-${day}T${hour}:${minute}:${second}`;
    }
  }

  return value;
}

export function isCommunicationEntry(entry: Record<string, unknown>): boolean {
  const typeID = String(entry.typeID ?? "");
  if (typeID === "17" || typeID === "19") {
    return cleanText(String(entry.notes ?? "")) !== "";
  }
  return true;
}

export function commlogEntryToMessage(
  entry: CommlogEntry | Record<string, unknown>,
  options?: { hideQuoted?: boolean; patientId?: string; patientName?: string; patientAvatar?: string },
): Message | null {
  try {
    return commlogEntryToMessageInner(entry, options);
  } catch (err) {
    console.error("Failed to parse comm log entry:", entry.commLogID ?? entry.id, err);
    return null;
  }
}

function commlogEntryToMessageInner(
  entry: CommlogEntry | Record<string, unknown>,
  options?: { hideQuoted?: boolean; patientId?: string; patientName?: string; patientAvatar?: string },
): Message {
  const typeID = String(entry.typeID ?? "");
  // An inbound SMS that arrived on the account's marketing number is a reply to
  // a campaign, so it groups with the marketing traffic rather than sitting in
  // the clinical SMS stream indistinguishable from it. The API sets this flag
  // by matching the stored receiving number against the marketing from-number.
  // It stays a real comm_log row, so staff can still reply to it as normal.
  const isMarketingReply = isTruthyFlag(entry.isMarketingReply);
  // Which channel the reply came back on. SMS replies arrived on the marketing
  // number; e-mail replies arrived at a per-campaign Reply-To address. The only
  // thing this changes on screen is whether the subject is worth showing (see
  // `subject` below), but that difference is not inferable from typeID alone —
  // both channels are promoted to the same "marketing" channel above.
  const marketingReplyChannel = String(entry.marketingReplyChannel ?? "");
  const channel: Message["channel"] = isMarketingReply
    ? "marketing"
    : (COMMLOG_TYPE_ID_MAP[typeID] ?? "sms");
  // A request made through a marketing link (typeID 27). Tagged with the medium
  // the link arrived on; a bare link that no send delivered has none.
  const isMarketingRequest = typeID === "27";
  const marketingChannel = String(entry.marketingChannel ?? "");
  // Which flavour of marketing this is — drives the bubble icon, and for e-mail
  // also restricts the inline preview to the preheader. A request with no
  // medium gets no kind, so it shows the megaphone alone and answers to the
  // Marketing pill only.
  const marketingKind: Message["marketingKind"] = isMarketingReply
    ? "reply"
    : channel === "marketing"
      ? (marketingChannel === "sms"
          ? "sms"
          : marketingChannel === "email" || !isMarketingRequest
            ? "email"
            : undefined)
      : undefined;
  const leadIdRaw = Number(entry.leadId ?? 0);
  const leadId = isMarketingRequest && Number.isFinite(leadIdRaw) && leadIdRaw > 0 && !isTruthyFlag(entry.leadDeleted)
    ? leadIdRaw
    : undefined;
  const isStageChange = typeID === "17" || typeID === "19";
  const isStreamEntry = entry.isStream === true || entry.isStream === "1" || entry.isStream === 1;
  const direction = String(entry.smsDirection ?? entry.direction ?? "").toLowerCase();
  const sender: Message["sender"] =
    isTruthyFlag(entry.fromPatient) ||
    direction.includes("inbound") ||
    direction.includes("incoming") ||
    direction.includes("received")
      ? "patient"
      : "provider";

  const subject = cleanText(firstNonEmpty(entry.emailSubject, entry.subject));
  const rawEmailBody = normalizeBody(firstNonEmpty(entry.emailBody));
  const emailBodyPreview = options?.hideQuoted
    ? normalizeBody(firstNonEmpty(entry.emailBody), true)
    : rawEmailBody;

  // Notes (plan notes, stage-change notes, web-lead intake) may carry the
  // legacy "!*******BEGIN RAW CONTENT*******!" delimiter: a short plain-text
  // preview followed by the original HTML body. Mirror legacy
  // lead_display_content.php — keep the preview inline and surface the raw
  // HTML through the "View full message" iframe, exactly like Email.
  const { preview: notesPreview, raw: notesRaw } = splitRawContent(firstNonEmpty(entry.notes));
  const rawNotes = normalizeBody(notesPreview);
  const notesPreviewText = options?.hideQuoted
    ? normalizeBody(notesPreview, true)
    : rawNotes;
  const smsMessage = normalizeBody(firstNonEmpty(entry.smsMessage), options?.hideQuoted);

  // htmlContent is used by the "View full email" modal — always show the
  // complete message including quoted text so the user can read context.
  // When the server truncated the body (hasFullBody), don't set htmlContent
  // from the incomplete text — it will be lazy-loaded on demand.
  const truncated = Boolean(entry.hasFullBody);
  // Marketing e-mails carry the full campaign/step HTML template in emailBody,
  // so they route through the same "View full email" iframe dialog. Marketing
  // SMS shares the channel but has no HTML body — isHtml() screens it out.
  const emailHtml =
    !truncated &&
    (channel === "email" || channel === "marketing") &&
    rawEmailBody &&
    isHtml(rawEmailBody)
      ? rawEmailBody
      : undefined;
  // For notes, prefer the post-delimiter raw body (always viewable, matching
  // legacy). Without a delimiter, fall back to treating an HTML note body as
  // rich content so Plan Notes render like Email.
  const noteRawHtml = notesRaw ? normalizeBody(notesRaw) : "";
  const noteHtml = !truncated && channel === "note"
    ? (notesRaw
        ? noteRawHtml || undefined
        : rawNotes && isHtml(rawNotes)
          ? rawNotes
          : undefined)
    : undefined;

  // "Patient Form Submitted" notes carry only a legacy anchor to the form
  // response. When we can resolve the surveysResponseID, open the V5
  // SurveyResponseModal instead of rendering the dead anchor in an iframe.
  // Prefer the id parsed server-side (available even when the list/timeline
  // endpoint strips the anchor HTML from the preview); fall back to parsing
  // the raw notes for callers that return the full body.
  const surveyResponseIdRaw = Number(entry.surveyResponseId ?? 0);
  const surveyResponseId = Number.isFinite(surveyResponseIdRaw) && surveyResponseIdRaw > 0
    ? surveyResponseIdRaw
    : parseSurveyResponseId(firstNonEmpty(entry.notes));

  const htmlContent = surveyResponseId ? undefined : (emailHtml ?? noteHtml);

  // Inline preview uses quote-stripped versions so replies don't run together.
  // A marketing e-mail is the exception: its only inline body is the preheader
  // (carried in notes). The campaign HTML is never a preview candidate — it is
  // a full styled template, and stripHtml() reads textContent, which includes
  // <style> rules, so falling back to it printed raw CSS into the bubble.
  const previewSource = channel === "email"
    ? firstNonEmpty(emailBodyPreview, notesPreviewText, smsMessage, subject)
    : marketingKind === "email"
      ? notesPreviewText
      : firstNonEmpty(notesPreviewText, smsMessage, subject, emailBodyPreview);

  const rawPreviewText = cleanText(previewSource);
  // An inbound e-mail reply carries the whole original message quoted beneath
  // the patient's words, so it needs the same header/chain truncation a normal
  // inbound e-mail gets. Its channel is "marketing", not "email", which is why
  // this cannot key off channel alone.
  const previewText = channel === "email" || marketingReplyChannel === "email"
    ? stripEmailHeaders(rawPreviewText)
    : rawPreviewText;
  const content = isStageChange
    ? buildStageChangeContent(typeID, cleanText(firstNonEmpty(entry.stageName)), notesPreviewText)
    : previewText
      ? previewText
      // The subject is already rendered as the bubble heading, so don't repeat
      // it as the body when a campaign has no preheader.
      : marketingKind === "email"
        ? "(No preview text)"
        : fallbackPreview(channel, subject, Boolean(htmlContent));

  const read =
    entry.wasRead === undefined || entry.wasRead === null
      ? true
      : isTruthyFlag(entry.wasRead);

  const rawRecipients = Array.isArray((entry as Record<string, unknown>).recipients)
    ? ((entry as Record<string, unknown>).recipients as Array<Record<string, unknown>>)
    : [];
  const recipients = rawRecipients
    .map((r) => ({
      userId: Number(r?.userId ?? 0),
      name: String(r?.name ?? "").trim(),
    }))
    .filter((r) => r.userId > 0);

  // Parsed exactly like recipients above: the raw entry is loosely typed, so
  // coerce each row and drop anything without a real user or timestamp rather
  // than rendering a blank line in an audit trail. A screen is kept only when
  // it is one the app knows; anything else is left off rather than shown.
  const rawReadHistory = Array.isArray((entry as Record<string, unknown>).readHistory)
    ? ((entry as Record<string, unknown>).readHistory as Array<Record<string, unknown>>)
    : [];
  const readHistory = rawReadHistory
    .map((r) => {
      const source = r?.source;
      return {
        userId: Number(r?.userId ?? 0),
        name: String(r?.name ?? "").trim(),
        readAt: String(r?.readAt ?? "").trim(),
        ...(isReadSource(source) ? { source } : {}),
      };
    })
    .filter((r) => r.userId > 0 && r.readAt !== "");

  const authorUserIdRaw = Number(entry.userId ?? 0);
  const authorUserId = Number.isFinite(authorUserIdRaw) && authorUserIdRaw > 0
    ? authorUserIdRaw
    : undefined;
  // System messages are outbound entries that have no human author
  // (userID = 0 in comm_log). Inbound patient messages also have userId 0
  // but sender === "patient", so we gate on the provider side. Stage changes
  // are never treated as system so they stay visible under the Note filter
  // even when auto-generated (userID = 0) and the system toggle is off.
  // Marketing sends are exempt for the same reason: they have no author at all
  // (they are not comm_log rows), and the Marketing pill is how they are meant
  // to be shown or hidden — the system toggle exists to mute automated clinical
  // traffic, not to make a patient's campaign history disappear.
  //
  // Subscription control words are the exception that DOES belong to the system
  // toggle. A bare "STOP" or "START" is an instruction to the SMS platform, not
  // something the patient said to the practice, and a patient who opts out and
  // back in repeatedly otherwise fills their history with one-word messages. The
  // API flags these against the account's own keyword list; they are inbound, so
  // the provider-side rule below would never have caught them.
  const isOptOutKeyword = isTruthyFlag(entry.isOptOutKeyword);
  const isSystem =
    isOptOutKeyword
    || (!isStageChange && !marketingKind && sender === "provider" && authorUserIdRaw === 0);

  return {
    id: String(entry.commLogID ?? entry.id ?? ""),
    content,
    htmlContent,
    timestamp: normalizeTimestamp(entry.date ?? entry.dateAdded),
    sender,
    senderName: authorNameForEntry(entry, sender, isStreamEntry, options?.patientName, marketingKind),
    senderAvatar: sender === "patient" ? options?.patientAvatar : undefined,
    authorUserId,
    type: "text",
    read,
    channel,
    marketingKind,
    marketingStatus: marketingKind
      ? MARKETING_STATUS_LABELS[String(entry.marketingDeliveryStatus ?? "").toLowerCase()]
      : undefined,
    // Set only when the message did not reach the patient. The API annotates
    // outbound rows only, and this re-checks the direction so an inbound
    // message can never be badged as undelivered — it is already here.
    deliveryFailure:
      isTruthyFlag(entry.deliveryFailed) && sender === "provider"
        ? {
            label: DELIVERY_FAILURE_LABEL,
            advice: deliveryFailureAdvice(
              String(entry.deliveryFailureKind ?? ""),
              String(entry.deliveryFailureReason ?? ""),
            ),
          }
        : undefined,
    replyID: entry.replyID && entry.replyID !== "0" ? String(entry.replyID) : undefined,
    // An SMS marketing reply's stored subject is boilerplate ("Incoming SMS
    // Message"); the teal styling and reply icon already identify it, so drop
    // the heading rather than render noise above the patient's actual words.
    // An E-MAIL reply is the opposite case: its subject is the patient's own
    // "Re: <campaign>", which is the clearest indication of what they are
    // replying to, so it must be kept.
    subject: isMarketingReply && marketingReplyChannel !== "email"
      ? undefined
      : (subject || undefined),
    attachmentLinks: parseAttachmentLinks(entry, options?.patientId),
    recipients: recipients.length > 0 ? recipients : undefined,
    readHistory: readHistory.length > 0 ? readHistory : undefined,
    canEdit: !isStreamEntry && !isStageChange,
    canDelete: !isStreamEntry && !isStageChange,
    canReply: !isStreamEntry && !isStageChange,
    isSystem,
    hasFullBody: truncated,
    commLogId: String(entry.commLogID ?? entry.id ?? ""),
    surveyResponseId,
    // A marketing request links out to the lead it created.
    leadId,
    // Which of the practice's own numbers this text used.
    //
    // Gated on the TEXT typeIDs, not on `channel`. `channel` is rewritten to
    // "marketing" for any reply the server badged as a campaign response (see
    // its derivation above), and a STOP sent to the marketing line is exactly
    // that — so keying on `channel === "sms"` dropped the number on the single
    // message this feature exists to answer. typeID 2/18 keeps those replies
    // while still excluding WhatsApp (22), whose wording and fallback are
    // SMS-shaped and would name a line it never used, and email (1/7).
    //
    // Always the number that message actually used; the server omits the key
    // entirely when nothing was recorded, and nothing is inferred from it.
    practiceNumber:
      (typeID === "2" || typeID === "18") &&
      typeof entry.practiceNumber === "string" &&
      entry.practiceNumber !== ""
        ? entry.practiceNumber
        : undefined,
  };
}

/**
 * Convert a HealthNexus provider↔patient chat message into a Comm Log
 * Message. Individual messages carry no edit/delete/reply actions — they live
 * in an external system, so there is nothing local to mutate. New messages go
 * out through the HealthNexus composer (useCommlog's sendHealthNexus), which
 * posts to the portal and refetches this history.
 */
export function healthNexusMessageToMessage(
  message: HealthNexusMessage,
  options?: { patientName?: string; patientAvatar?: string },
): Message {
  const sender: Message["sender"] = message.senderType === "patient" ? "patient" : "provider";
  const senderName = String(message.senderName ?? "").trim()
    || (sender === "patient" ? (options?.patientName?.trim() || "Patient") : "Provider");

  return {
    id: String(message.id ?? ""),
    content: cleanText(String(message.body ?? "")) || "(No message text)",
    timestamp: normalizeTimestamp(message.createdAt),
    sender,
    senderName,
    senderAvatar: sender === "patient" ? options?.patientAvatar : undefined,
    type: "text",
    read: true,
    channel: "healthnexus",
    canEdit: false,
    canDelete: false,
    canReply: false,
    isSystem: false,
  };
}
